// js/ModalManager.js

// Import dependencies
import GameCore from './GameCore.js';
import GameUI from './GameUI.js';
import AccessibilityManager from './core/AccessibilityManager.js';
import StorageManager from './StorageManager.js';
import PlayerStats from './stats.js';
import { track } from './telemetry.js';

export default class ModalManager { // Add export default
  /**
   * @param {GameCore} gameCore
   * @param {GameUI} gameUI
   * @param {AccessibilityManager} accessibilityManager
   * @param {StorageManager} storageManager
   * @param {PlayerStats} playerStats // Add PlayerStats instance
   */
  constructor(gameCore, gameUI, accessibilityManager, storageManager, playerStats) { // Add playerStats param
    // Type checking for dependencies
    if (!(gameCore instanceof GameCore)) throw new Error('ModalManager requires a GameCore instance.');
    if (!(gameUI instanceof GameUI)) throw new Error('ModalManager requires a GameUI instance.');
    if (!(accessibilityManager instanceof AccessibilityManager)) throw new Error('ModalManager requires an AccessibilityManager instance.');
    if (!(storageManager instanceof StorageManager)) throw new Error('ModalManager requires a StorageManager instance.');
    if (!(playerStats instanceof PlayerStats)) throw new Error('ModalManager requires a PlayerStats instance.'); // Check PlayerStats

    this.gameCore = gameCore;
    this.gameUI = gameUI;
    this.accessibilityManager = accessibilityManager;
    this.storageManager = storageManager;
    this.playerStats = playerStats; // Store the PlayerStats instance

    // Cache all modal elements
    this.modals = {
      victory: document.getElementById('victory-modal'),
      leaderboard: document.getElementById('leaderboard-modal'),
      accessibility: document.getElementById('accessibility-modal'),
      stats: document.getElementById('stats-modal'),
      share: document.getElementById('share-modal'),
      resetConfirm: null, // Dynamically created
      limitReached: null, // Dynamically created
      hardModeFail: null, // Dynamically created
      help: document.getElementById('help-modal'),
      customConfirmDanger: null, // For reset stats confirmation
      customConfirmInfo: null,   // For hard mode enable confirmation
    };

    // Cache relevant buttons within modals - ADDED OPTIONAL CHAINING ?.
    this.buttons = {
      // Victory
      closeVictory: this.modals.victory?.querySelector('#close-modal'),
      shareResult: this.modals.victory?.querySelector('#share-button'),
      // Leaderboard
      closeLeaderboard: this.modals.leaderboard?.querySelector('#close-leaderboard'),
      leaderboardTabs: this.modals.leaderboard?.querySelectorAll('.tab-button'),
      leaderboardModeTabs: this.modals.leaderboard?.querySelectorAll('.mode-tab-button'),
      // Accessibility
      closeAccessibility: this.modals.accessibility?.querySelector('#close-accessibility'),
      highContrastCheckbox: this.modals.accessibility?.querySelector('#high-contrast-mode'),
      largerTextCheckbox: this.modals.accessibility?.querySelector('#larger-text'),
      screenReaderCheckbox: this.modals.accessibility?.querySelector('#screen-reader-mode'),
      reduceMotionCheckbox: this.modals.accessibility?.querySelector('#reduce-motion'),
      // Stats
      closeStats: this.modals.stats?.querySelector('#close-stats'),
      resetStats: this.modals.stats?.querySelector('#reset-stats'),
      statsTabs: this.modals.stats?.querySelectorAll('.stats-tab-button'), // Cache tabs
      // Share (if static in HTML)
      closeShare: this.modals.share?.querySelector('#close-share'),
      // Help
      closeHelp: this.modals.help?.querySelector('#close-help'),
    };

    // Cache elements needing dynamic content - ADDED OPTIONAL CHAINING ?.
    this.dynamicElements = {
      // Victory
      finalSwaps: this.modals.victory?.querySelector('#final-swaps'),
      victoryTitle: this.modals.victory?.querySelector('#victory-title'),
      // Leaderboard
      leaderboardContent: this.modals.leaderboard?.querySelector('#leaderboard-content'),
      // Stats
      statsContentArea: this.modals.stats?.querySelector('#stats-content-area'),
      statsGamesPlayed: this.modals.stats?.querySelector('#stats-games-played'),
      statsCompletionRate: this.modals.stats?.querySelector('#stats-completion-rate'),
      statsAvgSwaps: this.modals.stats?.querySelector('#stats-avg-swaps'),
      statsBestSwaps: this.modals.stats?.querySelector('#stats-best-swaps'),
      statsCurrentStreak: this.modals.stats?.querySelector('#stats-current-streak'),
      statsMaxStreak: this.modals.stats?.querySelector('#stats-max-streak'),
      statsCalendarContainer: this.modals.stats?.querySelector('#stats-calendar-container'),
      statsHardModeWins: this.modals.stats?.querySelector('#stats-hard-mode-wins'),
      statsCalendarTitle: this.modals.stats?.querySelector('#stats-calendar-title'),
      // Share (if static)
      shareTextBox: this.modals.share?.querySelector('.share-text-box'),
      shareRankInfo: this.modals.share?.querySelector('.share-rank-info'),
      shareCopyNotification: this.modals.share?.querySelector('.copy-notification'),
    };

    // --- DEBUG LOG: Check cached stats elements (KEEP THIS) ---
    // console.log('[ModalManager Constructor] Cached dynamic stats elements:', { /* ... */ });
    // --- END DEBUG LOG ---

    // Define triggerButtons AFTER the potentially problematic assignments above
    this.triggerButtons = {
      // These buttons are now INSIDE the menu, but the IDs are the same
      leaderboard: document.getElementById('leaderboard-button'),
      accessibility: document.getElementById('accessibility-button'),
      stats: document.getElementById('stats-button'),
      help: document.getElementById('help-button'),
      // Keep hint button reference if needed? No, hint button is outside menu.
      // reset: document.getElementById('reset-button'), // Reset is hidden
    };

    this.openModalStack = [];
    this.boundHandleKeydown = this._handleKeydown.bind(this);

    this.showMessage = (message, _duration) => console.warn('showMessage function not set', message);
    this.currentStatsSize = this.gameCore.gridSize;

    this._bindStaticListeners(); // Now called after triggerButtons is safely defined
    this._addBaseStyles();
  }

  setMessageDisplayFunction(func) {
    if (typeof func === 'function') {
      this.showMessage = func;
    }
  }

  _addBaseStyles() {
    if (!document.getElementById('modal-base-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'modal-base-styles';
      styleSheet.textContent = `
         .modal { display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.8); z-index: 100; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.3s ease; pointer-events: none; }
         .modal.active { display: flex; opacity: 1; pointer-events: auto; }
         .modal-content { background-color: #1e1e1e; padding: 30px; border-radius: 10px; text-align: center; max-width: 500px; width: 90%; max-height: 90vh; overflow-y: auto; transform: scale(0.9); transition: transform 0.3s ease; box-shadow: 0 5px 20px rgba(0,0,0,0.4); }
         .modal.active .modal-content { transform: scale(1); }
         .modal-content:focus { outline: none; }
         .modal button { background-color: var(--button-color, #424242); color: var(--text-color, #FFFFFF); border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; font-size: 1rem; transition: background-color 0.3s; margin: 5px; }
         .modal button:hover { background-color: var(--button-hover, #616161); }
         .modal button:focus { outline: 3px solid var(--focus-outline, #3498db); outline-offset: 2px; }
       `;
      document.head.appendChild(styleSheet);
    }
  }

  _bindStaticListeners() {
    // Trigger buttons (inside the menu) - These should still work using getElementById
    this.triggerButtons.leaderboard?.addEventListener('click', () => this.showLeaderboard());
    this.triggerButtons.accessibility?.addEventListener('click', () => this.showModal('accessibility'));
    this.triggerButtons.stats?.addEventListener('click', () => this.showStatsModal());
    this.triggerButtons.help?.addEventListener('click', () => this.showModal('help'));

    // Close buttons for static modals
    this.buttons.closeVictory?.addEventListener('click', () => this.hideModal('victory'));
    this.buttons.closeLeaderboard?.addEventListener('click', () => this.hideModal('leaderboard'));
    this.buttons.closeAccessibility?.addEventListener('click', () => this.hideModal('accessibility'));
    // this.buttons.closeAccessibility?.addEventListener('click', () => this.hideModal('accessibility')); // Moved above with check
    this.buttons.closeStats?.addEventListener('click', () => this.hideModal('stats'));
    this.buttons.closeShare?.addEventListener('click', () => this.hideModal('share'));
    this.buttons.closeHelp?.addEventListener('click', () => this.hideModal('help'));

    // Specific modal listeners
    this._bindLeaderboardListeners();
    this._bindAccessibilityListeners();
    this._bindStatsListeners();
    this._bindShareListeners();

    // --- Add specific checks for button existence before binding ---
    if (!this.buttons.closeAccessibility) {
        console.warn("ModalManager: Close button for Accessibility modal not found during listener binding.");
    } else {
        this.buttons.closeAccessibility.addEventListener('click', () => this.hideModal('accessibility'));
    }
    // (Similar checks could be added for other buttons if needed)
    // --- End specific checks ---


    // Share button in victory modal
    this.buttons.shareResult?.addEventListener('click', () => {
      track('share_opened', { grid_size: this.gameCore.gridSize, puzzle_date: this.gameCore.todayDate });
      // If share opens OVER victory, don't hide victory yet
      this.showModal('share');
    });

    // Close modal on backdrop click
    Object.entries(this.modals).forEach(([modalId, modal]) => {
      // Exclude dynamically created modals and share modal (since it can overlay victory)
      const nonBackdropCloseModals = ['resetConfirm', 'limitReached', 'hardModeFail', 'customConfirmDanger', 'customConfirmInfo', 'share'];
      if (modal && !nonBackdropCloseModals.includes(modalId)) {
        modal.addEventListener('click', (event) => {
          if (event.target === modal) {
            this.hideModal(modalId);
          }
        });
      }
    });
  }


  isModalOpen() {
    return this.openModalStack.length > 0;
  }

  closeTopModal() {
    if (this.openModalStack.length > 0) {
      const topModalId = this.openModalStack[this.openModalStack.length - 1];
      this.hideModal(topModalId);
    }
  }

  showModal(modalId, options = {}) {
    const modal = this.modals[modalId];
    if (!modal) {
      console.error(`Modal with ID '${modalId}' not found.`);
      return;
    }

    // Allow share modal over victory modal, otherwise close existing modals
    if (this.isModalOpen() && !this.openModalStack.includes(modalId)) {
      const isShareOverVictory = modalId === 'share' && this.openModalStack.includes('victory');
      if (!isShareOverVictory) {
        console.warn(`Attempting to open modal '${modalId}' while another modal is active. Closing top modal first.`);
        this.closeTopModal();
        // Re-queue the modal show slightly delayed to allow hide transition
        setTimeout(() => this.showModal(modalId, options), 350); // Increased delay slightly
        return;
      }
      console.log(`Opening 'share' modal over 'victory' modal.`);
    }

    // Prepare modal content based on ID
    switch (modalId) {
      case 'victory':
        this._prepareVictoryModal(options.isRestoredState, options.isHardMode);
        break;
      case 'leaderboard':
        this._prepareLeaderboardModal(options.gridSize || this.gameCore.gridSize);
        break;
      case 'accessibility':
        this._prepareAccessibilityModal();
        break;
      case 'stats':
        this.currentStatsSize = options.gridSize || this.gameCore.gridSize; // Use option or current game size
        console.log(`[showModal] Preparing stats modal for size: ${this.currentStatsSize}`);
        this._prepareStatsModal(this.currentStatsSize);
        break;
      case 'share':
        this._prepareShareModal();
        break;
      case 'resetConfirm':
      case 'limitReached':
      case 'hardModeFail':
      case 'help':
      case 'customConfirmDanger':
      case 'customConfirmInfo':
        // These modals are either dynamically generated or static content
        break;
      default:
        console.warn(`No preparation logic defined for modal ID '${modalId}'.`);
    }

    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');

    this._focusTrap(modal);

    // Push to stack if not already present
    if (!this.openModalStack.includes(modalId)) {
      this.openModalStack.push(modalId);
    }

    // Add global keydown listener only when the first modal opens
    if (this.openModalStack.length === 1) {
      document.addEventListener('keydown', this.boundHandleKeydown);
    }

    const titleElement = modal.querySelector('h2');
    const title = titleElement ? titleElement.textContent : `${modalId} modal`;
    this.accessibilityManager.announce(`${title} opened`, true);
  }

  hideModal(modalId) {
    const modal = this.modals[modalId];
    if (!modal || !modal.classList.contains('active')) {
      return; // Exit if modal not found or not active
    }

    // Move focus out before hiding to prevent aria-hidden focus warning
    if (modal.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    this._removeFocusTrap(modal);

    const index = this.openModalStack.indexOf(modalId);
    if (index > -1) {
      this.openModalStack.splice(index, 1);
    }

    // --- Focus Return Logic - UPDATED for Menu ---
    if (this.openModalStack.length === 0) { // Only run if this was the last modal
      // Check if the menu is currently open (use global var or check class)
      // Assume menuIsOpen variable is accessible (passed or global) or check class
      const slideoutMenu = document.getElementById('slideout-menu');
      const menuIsOpen = slideoutMenu?.classList.contains('open');
      const menuToggleButton = document.getElementById('menu-toggle-button');

      let returnFocusTo = null;

      // If the menu is CLOSED, focus should go back to the toggle button
      // (assuming the modal was likely opened from the menu)
      if (!menuIsOpen && menuToggleButton) {
        returnFocusTo = menuToggleButton;
      }
      // If the menu is still OPEN (less common, maybe share modal closed?),
      // let focus remain within the menu (or focus first menu item as fallback)
      else if (menuIsOpen) {
        const firstMenuItem = slideoutMenu?.querySelector('button[role="menuitem"]');
        if (firstMenuItem) {
          returnFocusTo = firstMenuItem;
        }
      }
      // Fallback: If menu logic isn't involved, try original logic (less likely now)
      else {
        const originalTriggerButton = Object.entries(this.triggerButtons).find(([key, btn]) => key === modalId)?.[1]
         || document.querySelector(`[aria-controls="${modalId}-modal"]`)
         || document.querySelector(`button[id*="${modalId}"]`);

         if (originalTriggerButton && typeof originalTriggerButton.focus === 'function' && originalTriggerButton.offsetParent !== null) {
             returnFocusTo = originalTriggerButton;
         }
      }

      // Perform the focus action
      if (returnFocusTo) {
        setTimeout(() => returnFocusTo.focus(), 50); // Use timeout to allow transitions
      } else {
        document.body.focus(); // Ultimate fallback
      }

    } else {
      // Focus remains within the next modal down the stack
      const newTopModalId = this.openModalStack[this.openModalStack.length - 1];
      const newTopModal = this.modals[newTopModalId];
      if (newTopModal) {
        const firstFocusable = newTopModal.querySelector('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) setTimeout(() => firstFocusable.focus(), 50);
        else setTimeout(() => newTopModal.querySelector('.modal-content')?.focus(), 50);
      }
    }
    // --- End Focus Return Logic ---

    // Remove global keydown listener only when the last modal closes
    if (this.openModalStack.length === 0) {
      document.removeEventListener('keydown', this.boundHandleKeydown);
    }

    // Cleanup for dynamically created modals
    if (modalId === 'resetConfirm' || modalId === 'limitReached' || modalId === 'hardModeFail' || modalId === 'customConfirmDanger' || modalId === 'customConfirmInfo') {
      if (modal.parentNode) {
        modal.remove();
      }
      this.modals[modalId] = null; // Remove reference
    }

    const titleElement = modal.querySelector('h2');
    const title = titleElement ? titleElement.textContent : `${modalId} modal`;
    this.accessibilityManager.announce(`${title} closed`, false);
  }


  closeAllModals() {
    // Iterate copy as hideModal modifies the stack
    [...this.openModalStack].forEach((modalId) => this.hideModal(modalId));
  }

  _handleKeydown(event) {
    // Close only the top-most modal on Escape
    if (event.key === 'Escape' && this.openModalStack.length > 0) {
      event.preventDefault();
      this.closeTopModal();
    }
  }

  _focusTrap(modal) {
    // Filter only visible focusable elements
    const focusableElements = Array.from(
      modal.querySelectorAll(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => el.offsetParent !== null); // Check if visible

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus logic prioritizes specific elements, then falls back
    setTimeout(() => {
      const primaryAction = modal.querySelector('#custom-confirm-yes, #try-again-button, #share-button, .stats-tab-button.active, .tab-button.active');
      const secondaryAction = modal.querySelector('[id^="close-"], #custom-confirm-cancel, .stats-tab-button:not(.active), .tab-button:not(.active)'); // Prefer non-active tabs as secondary
      const firstInput = modal.querySelector('input, select, textarea');

      // Check visibility before focusing
      const focusTarget = [primaryAction, secondaryAction, firstInput, firstElement, modal.querySelector('.modal-content')]
                          .find(el => el && el.offsetParent !== null);

      focusTarget?.focus();

    }, 100); // Ensure transition completes

    const trapListener = (event) => {
      if (event.key === 'Tab') {
        // Recalculate visible focusable elements inside the listener
        const currentFocusableElements = Array.from(
          modal.querySelectorAll(
            'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter(el => el.offsetParent !== null);

        if (currentFocusableElements.length === 0) return;
        const currentFirstElement = currentFocusableElements[0];
        const currentLastElement = currentFocusableElements[currentFocusableElements.length - 1];

        if (!modal.contains(document.activeElement)) {
            currentFirstElement.focus();
            event.preventDefault();
            return;
        }

        if (event.shiftKey) { // Shift + Tab
          if (document.activeElement === currentFirstElement) {
            currentLastElement.focus();
            event.preventDefault();
          }
        } else { // Tab
          if (document.activeElement === currentLastElement) {
            currentFirstElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    modal.addEventListener('keydown', trapListener);
    modal.__focusTrapListener = trapListener; // Store reference for removal
  }


  _removeFocusTrap(modal) {
    if (modal && modal.__focusTrapListener) {
      modal.removeEventListener('keydown', modal.__focusTrapListener);
      delete modal.__focusTrapListener;
    }
  }

  // --- Victory Modal Logic ---
  _prepareVictoryModal(isRestoredState = false, isHardMode = false) {
    if (!this.modals.victory || !this.dynamicElements.finalSwaps || !this.dynamicElements.victoryTitle)
      return;

    this.dynamicElements.finalSwaps.textContent = this.gameCore.puzzle.swapCount;

    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateString = today.toLocaleDateString(undefined, options);

    let titleText;
    if (isRestoredState) {
      titleText = `You've Completed Today's Puzzle!`;
    } else if (this.gameCore.isUnlimitedMode) {
      titleText = `Puzzle Solved in Unlimited Mode!`;
    } else if (isHardMode) {
      titleText = `Hard Mode Puzzle Solved!`;
    } else {
      titleText = `Daily Puzzle Solved!`;
    }
    this.dynamicElements.victoryTitle.innerHTML = `${titleText} <span style="font-size: 0.8em; color: #FFD700;">(${dateString})</span>`;

    // Clear previous badges/info
    this.modals.victory.querySelector('.unlimited-mode-info')?.remove();
    this.modals.victory.querySelector('.hard-mode-badge')?.remove();

    const insertAfter = this.dynamicElements.finalSwaps?.closest('p');
    const modalContent = this.modals.victory?.querySelector('.modal-content');

    if (this.gameCore.isUnlimitedMode) {
      const unlimitedInfoElement = document.createElement('p');
      unlimitedInfoElement.className = 'unlimited-mode-info';
      unlimitedInfoElement.innerHTML =
        '<em>Completed in unlimited mode (score not eligible for leaderboard)</em>';
      unlimitedInfoElement.style.cssText = 'font-size: 0.9rem; color: #888; margin-top: 10px;';
      if (insertAfter) insertAfter.insertAdjacentElement('afterend', unlimitedInfoElement);
      else if (modalContent) modalContent.appendChild(unlimitedInfoElement);
    } else if (isHardMode) {
      const hardModeBadge = document.createElement('div');
      hardModeBadge.className = 'hard-mode-badge';
      hardModeBadge.innerHTML = '<i class="fas fa-trophy"></i> Hard Mode Completed!';
      if (insertAfter) insertAfter.insertAdjacentElement('afterend', hardModeBadge);
      else if (modalContent) modalContent.appendChild(hardModeBadge);
    }
  }

  // --- Leaderboard Modal Logic ---
  _bindLeaderboardListeners() {
    this._leaderboardMode = 'regular';

    this.buttons.leaderboardTabs?.forEach((button) => {
      button.addEventListener('click', () => {
        const size = parseInt(button.dataset.size);
        this.updateLeaderboard(size, this._leaderboardMode);
        this.buttons.leaderboardTabs.forEach(btn => {
          const isActive = btn === button;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        const contentArea = this.dynamicElements.leaderboardContent;
        if (contentArea) contentArea.setAttribute('aria-labelledby', button.id);
      });
    });

    this.buttons.leaderboardModeTabs?.forEach((button) => {
      button.addEventListener('click', () => {
        this._leaderboardMode = button.dataset.mode;
        const activeSize = this.modals.leaderboard?.querySelector('.tab-button.active');
        const size = activeSize ? parseInt(activeSize.dataset.size) : this.gameCore.gridSize;
        this.updateLeaderboard(size, this._leaderboardMode);
        this.buttons.leaderboardModeTabs.forEach(btn => {
          const isActive = btn === button;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      });
    });
  }

  _prepareLeaderboardModal(initialSize) {
    this.buttons.leaderboardTabs?.forEach((button) => {
      const isActive = parseInt(button.dataset.size) === initialSize;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive && this.dynamicElements.leaderboardContent) {
        this.dynamicElements.leaderboardContent.setAttribute('aria-labelledby', button.id);
      }
    });
    this.buttons.leaderboardModeTabs?.forEach((button) => {
      const isActive = button.dataset.mode === (this._leaderboardMode || 'regular');
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.updateLeaderboard(initialSize, this._leaderboardMode || 'regular');
  }

  showLeaderboard() {
    const currentSize = this.gameCore.gridSize;
    // this.updateLeaderboard(currentSize); // _prepare will call this
    this.showModal('leaderboard', { gridSize: currentSize });
  }

  async updateLeaderboard(size = this.gameCore.gridSize, mode = 'regular') {
    if (!this.modals.leaderboard || !this.dynamicElements.leaderboardContent) return;

    this.dynamicElements.leaderboardContent.innerHTML = '<p>Loading leaderboard...</p>';

    this.buttons.leaderboardTabs?.forEach((button) => {
      button.classList.toggle('active', parseInt(button.dataset.size) === size);
    });

    try {
      const scores = await this.storageManager.getLeaderboard(size, mode);
      this.dynamicElements.leaderboardContent.innerHTML = ''; // Clear loading message

      if (scores.length === 0) {
        this.dynamicElements.leaderboardContent.innerHTML = `<p>No scores yet for today's ${size}x${size} puzzle!</p>`;
      } else {
        const list = document.createElement('ol');
        list.style.cssText = 'list-style: none; padding: 0; margin: 0; text-align: left;';
        scores.forEach((score, index) => {
          const entry = document.createElement('li');
          entry.className = 'leaderboard-entry';
          
          // Map backend fields to frontend expectations
          const playerName = score.playerName || score.player || 'Anon';
          const swapCount = score.score !== undefined ? score.score : score.swaps;
          const isHardMode = score.isHardMode !== undefined ? score.isHardMode : score.hardMode;

          const rankSpan = document.createElement('span');
          rankSpan.style.cssText = 'display: inline-block; width: 25px; text-align: right; margin-right: 10px;';
          rankSpan.textContent = (index + 1) + '.';

          const nameSpan = document.createElement('span');
          nameSpan.style.cssText = 'font-weight: bold;';
          nameSpan.textContent = playerName;

          if (isHardMode) {
            const fireIcon = document.createElement('i');
            fireIcon.className = 'fas fa-fire';
            fireIcon.style.color = '#c0392b';
            fireIcon.title = 'Hard Mode';
            fireIcon.setAttribute('aria-hidden', 'true');
            nameSpan.appendChild(document.createTextNode(' '));
            nameSpan.appendChild(fireIcon);
          }

          const swapSpan = document.createElement('span');
          swapSpan.style.cssText = 'float: right;';
          swapSpan.textContent = swapCount + ' swaps';

          entry.appendChild(rankSpan);
          entry.appendChild(nameSpan);
          entry.appendChild(swapSpan);
          list.appendChild(entry);
        });
        this.dynamicElements.leaderboardContent.appendChild(list);
      }
    } catch (error) {
      console.error("Error updating leaderboard:", error);
      this.dynamicElements.leaderboardContent.innerHTML = `<p>Error loading leaderboard.</p>`;
    }
  }

  // --- Accessibility Modal Logic ---
  _bindAccessibilityListeners() {
    const updateSetting = (checkbox, property) => {
      checkbox?.addEventListener('change', () => {
        if (this.accessibilityManager) {
          this.accessibilityManager[property] = checkbox.checked;
          this.accessibilityManager.applySettings(); // Apply immediately
        }
      });
    };

    updateSetting(this.buttons.highContrastCheckbox, 'isHighContrastMode');
    updateSetting(this.buttons.largerTextCheckbox, 'isLargerTextMode');
    updateSetting(this.buttons.screenReaderCheckbox, 'isScreenReaderMode');
    updateSetting(this.buttons.reduceMotionCheckbox, 'isReducedMotionMode');

    // Close button still uses hideModal logic
    // this.buttons.closeAccessibility?.addEventListener('click', () => { /* Handled by generic hideModal */ });
  }

  _prepareAccessibilityModal() {
    // Load current settings and set checkbox states
    if (this.accessibilityManager) {
      this.accessibilityManager.loadSettings(); // Ensure latest settings are loaded
      const setChecked = (checkbox, value) => { if (checkbox) checkbox.checked = value; };
      setChecked(this.buttons.highContrastCheckbox, this.accessibilityManager.isHighContrastMode);
      setChecked(this.buttons.largerTextCheckbox, this.accessibilityManager.isLargerTextMode);
      setChecked(this.buttons.screenReaderCheckbox, this.accessibilityManager.isScreenReaderMode);
      setChecked(this.buttons.reduceMotionCheckbox, this.accessibilityManager.isReducedMotionMode);
    }
  }

  // --- Stats Modal Logic ---
  _bindStatsListeners() {
    // Tab switching
    this.buttons.statsTabs?.forEach(button => {
      button.addEventListener('click', () => {
        const size = parseInt(button.dataset.size);
        if (size === this.currentStatsSize && this.modals.stats?.classList.contains('active')) {
          return; // Avoid re-loading if already active
        }
        console.log(`[Stats Tab Click] Switching to size: ${size}`);
        this.currentStatsSize = size;
        this._loadAndDisplayStatsForSize(size);
        this._updateStatsCalendar(size);

        // Update tab button states
        this.buttons.statsTabs.forEach(btn => {
          const isActive = parseInt(btn.dataset.size) === size;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
          btn.tabIndex = isActive ? 0 : -1; // Manage focus order
        });

        // Update aria-labelledby for content
        const contentArea = this.dynamicElements.statsContentArea;
        if (contentArea) {
           contentArea.setAttribute('aria-labelledby', button.id);
        }

        this.accessibilityManager?.announce(`Showing ${size}x${size} stats.`, false);
      });
    });

    // Reset Stats button
    this.buttons.resetStats?.addEventListener('click', () => {
      this.showCustomConfirm(
        'Reset All Stats?',
        'Are you sure you want to reset all your stats (both 4x4 and 5x5)? This cannot be undone.',
        () => { // onConfirm
          if (this.storageManager.resetPlayerStats()) {
            console.log(`[Reset Stats] Reset successful. Reloading stats for currently viewed size: ${this.currentStatsSize}`);
            this._loadAndDisplayStatsForSize(this.currentStatsSize); // Reload current view
            this._updateStatsCalendar(this.currentStatsSize);
            this.showMessage('All your stats have been reset!', 3000);
            this.accessibilityManager?.announce('All stats reset.', true);
          } else {
            this.showMessage('Error resetting stats.', 3000);
          }
        },
        () => { // onCancel
          this.accessibilityManager?.announce('Stats reset cancelled.', false);
        },
        'Yes, Reset All', // confirmText
        'Cancel', // cancelText
        true // danger = true
      );
    });
  }

  showStatsModal() {
    this.currentStatsSize = this.gameCore.gridSize; // Ensure modal opens with current game size
    console.log(`[showStatsModal] Opening stats modal. Initial size: ${this.currentStatsSize}`);
    this.showModal('stats', { gridSize: this.currentStatsSize }); // Pass size explicitly
  }

  _prepareStatsModal(initialSize) {
    console.log(`[prepareStatsModal] Preparing for size: ${initialSize}`);
    // Set initial active tab
    this.buttons.statsTabs?.forEach(btn => {
      const buttonSize = parseInt(btn.dataset.size);
      const isActive = buttonSize === initialSize;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;

      // Set initial aria-labelledby for content area
      if (isActive && this.dynamicElements.statsContentArea) {
        this.dynamicElements.statsContentArea.setAttribute('aria-labelledby', btn.id);
      }
    });

    // Load initial data
    this._loadAndDisplayStatsForSize(initialSize);
    this._updateStatsCalendar(initialSize);
  }


  _loadAndDisplayStatsForSize(gridSize) {
    if (!this.modals.stats) return;
    console.log(`[_loadAndDisplayStatsForSize] Loading stats for ${gridSize}x${gridSize}`);

    let statsData;
    // Use the stored PlayerStats instance
    if (this.playerStats && typeof this.playerStats.loadStatsForSize === 'function') {
        statsData = this.playerStats.loadStatsForSize(gridSize);
        console.log(`[_loadAndDisplayStatsForSize] Loaded data via PlayerStats instance method:`, JSON.stringify(statsData));
    } else {
        // Fallback: Load directly if PlayerStats instance or method is unavailable
        console.warn("[_loadAndDisplayStatsForSize] PlayerStats instance or loadStatsForSize method not available, loading directly from storage.");
        const savedStats = this.storageManager.loadPlayerStats(gridSize);
        // Replicate defaults or ensure PlayerStats is available for _getDefaults
        const defaultStats = { gamesPlayed: 0, gamesWon: 0, totalSwaps: 0, bestScore: Infinity, currentStreak: 0, maxStreak: 0, hardModeWins: 0, playHistory: {} };
        statsData = { ...defaultStats, ...savedStats };

        // Perform sanitization manually if loaded directly
        statsData.gamesPlayed = parseInt(statsData.gamesPlayed) || 0;
        statsData.gamesWon = parseInt(statsData.gamesWon) || 0;
        statsData.totalSwaps = parseInt(statsData.totalSwaps) || 0;
        statsData.bestScore = parseFloat(statsData.bestScore);
        if (isNaN(statsData.bestScore) || statsData.bestScore === null) statsData.bestScore = Infinity;
        statsData.currentStreak = parseInt(statsData.currentStreak) || 0;
        statsData.maxStreak = parseInt(statsData.maxStreak) || 0;
        statsData.hardModeWins = parseInt(statsData.hardModeWins) || 0;
        if (typeof statsData.playHistory !== 'object' || statsData.playHistory === null) {
            statsData.playHistory = {};
        }
        Object.values(statsData.playHistory).forEach(entry => delete entry.gridSize); // Legacy cleanup
        console.log(`[_loadAndDisplayStatsForSize] Loaded data directly from storage (Fallback):`, JSON.stringify(statsData));
    }

    // Update the display with the loaded data
    this._updateStatsDisplay(statsData);

    // Update aria-labelledby for content area (ensure it matches the current tab)
    const contentArea = this.dynamicElements.statsContentArea;
    const tabButtonId = `stats-tab-${gridSize}x${gridSize}`;
    if (contentArea && document.getElementById(tabButtonId)) {
      contentArea.setAttribute('aria-labelledby', tabButtonId);
    } else if (contentArea) {
      console.warn(`[_loadAndDisplayStatsForSize] Tab button with ID ${tabButtonId} not found for aria-labelledby.`);
    }
  }

  _updateStatsDisplay(statsData) {
    console.log(`[_updateStatsDisplay] Attempting to display stats:`, JSON.stringify(statsData));
    if (!this.modals.stats || !statsData) {
      console.warn("[_updateStatsDisplay] Aborting: Modal or statsData missing.");
      return;
    }

    const completionRate = statsData.gamesPlayed > 0
      ? Math.round((statsData.gamesWon / statsData.gamesPlayed) * 100)
      : 0;
    const avgSwaps = statsData.gamesWon > 0
      ? Math.round((statsData.totalSwaps / statsData.gamesWon) * 10) / 10
      : 0;

    const setText = (element, text) => {
        if (element) { element.textContent = text; }
        else { console.warn("[_updateStatsDisplay -> setText] Element is null or undefined for text:", text); }
    };

    // Use nullish coalescing for safer defaults
    setText(this.dynamicElements.statsGamesPlayed, statsData.gamesPlayed ?? '0');
    setText(this.dynamicElements.statsCompletionRate, `${completionRate}%`);
    setText(this.dynamicElements.statsAvgSwaps, avgSwaps);
    setText(
      this.dynamicElements.statsBestSwaps,
      statsData.bestScore !== Infinity && statsData.bestScore != null ? statsData.bestScore : '-'
    );
    setText(this.dynamicElements.statsCurrentStreak, statsData.currentStreak ?? '0');
    setText(this.dynamicElements.statsMaxStreak, statsData.maxStreak ?? '0');
    setText(this.dynamicElements.statsHardModeWins, statsData.hardModeWins ?? '0');

    console.log("[_updateStatsDisplay] Finished updating text content.");
  }


  _updateStatsCalendar(gridSize) {
    const container = this.dynamicElements.statsCalendarContainer;
    const calendarTitle = this.dynamicElements.statsCalendarTitle;
    console.log(`[_updateStatsCalendar] Updating calendar for size: ${gridSize}`);

    if (!container || !calendarTitle) {
      console.warn("[_updateStatsCalendar] Cannot update stats calendar: container or title element not found.");
      if (container) container.innerHTML = '<p>Could not load calendar data.</p>';
      if (calendarTitle) calendarTitle.textContent = 'Last 7 Days History';
      return;
    }
    // Use the stored PlayerStats instance
     if (!this.playerStats || typeof this.playerStats.loadStatsForSize !== 'function') {
         console.warn("[_updateStatsCalendar] Cannot update stats calendar: PlayerStats instance or load method missing.");
         container.innerHTML = '<p>Could not load calendar data.</p>';
         calendarTitle.textContent = `Last 7 Days (${gridSize}x${gridSize})`;
         return;
     }

    calendarTitle.textContent = `Last 7 Days (${gridSize}x${gridSize})`;
    container.innerHTML = ''; // Clear previous calendar

    // Use the stored PlayerStats instance
    const statsData = this.playerStats.loadStatsForSize(gridSize);
    const history = statsData?.playHistory || {};
    console.log(`[_updateStatsCalendar] History for ${gridSize}x${gridSize}:`, JSON.stringify(history));

    const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();

      const dayElement = document.createElement('div');
      dayElement.className = 'calendar-day';

      const dayLabel = document.createElement('div');
      dayLabel.className = 'day-label';
      dayLabel.textContent = dayLabels[dayOfWeek];

      const dayContent = document.createElement('span');
      // Add styles directly for simplicity here, could be moved to CSS
      Object.assign(dayContent.style, { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' });

      if (dateString === todayStr) { dayElement.classList.add('today'); }

      let titleText = `Not played ${dateString} (${gridSize}x${gridSize})`;
      const dayHistory = history[dateString];

      if (dayHistory) {
        if (dayHistory.completed) {
          dayElement.classList.add('completed');
          titleText = `Completed ${dateString} (${gridSize}x${gridSize}) in ${dayHistory.swaps} swaps`;
          dayContent.appendChild(document.createTextNode(dayHistory.swaps));

          if (dayHistory.hardMode) {
            titleText += ' (Hard Mode)';
            const starIcon = document.createElement('i');
            starIcon.className = 'fas fa-star hard-mode-star';
            starIcon.setAttribute('aria-hidden', 'true');
            dayContent.appendChild(starIcon);
          }

        } else {
          dayElement.classList.add('failed');
          titleText = `Played ${dateString} (${gridSize}x${gridSize}) (not completed)`;
          dayContent.textContent = '✗';
        }
      } else {
        dayElement.classList.add('not-played');
        dayContent.textContent = '-';
      }

      dayElement.setAttribute('title', titleText);
      dayElement.appendChild(dayContent);
      dayElement.appendChild(dayLabel);
      container.appendChild(dayElement);
    }
    console.log("[_updateStatsCalendar] Finished calendar update.");
  }

  // --- Share Modal Logic ---
  _bindShareListeners() {
    if (!this.modals.share) return;

    const textBox = this.dynamicElements.shareTextBox;
    const notification = this.dynamicElements.shareCopyNotification;

    textBox?.addEventListener('click', () => {
      const textToCopy = textBox.textContent || '';
      const showSuccessNotification = () => {
        track('share_copied', { grid_size: this.gameCore.gridSize, puzzle_date: this.gameCore.todayDate });
        if (notification) {
          notification.style.visibility = 'visible';
          notification.style.opacity = '1';
          setTimeout(() => {
            notification.style.opacity = '0';
            setTimeout(() => { notification.style.visibility = 'hidden'; }, 300); // Match transition duration
          }, 2000);
        }
        this.accessibilityManager.announce('Result copied to clipboard!', false);
      };
      const showFailureNotification = (err) => {
          console.warn('Copy failed:', err);
          this.accessibilityManager.announce('Could not copy text. Please copy manually.', true);
          // Optionally show a more visible error message
          this.showMessage("Failed to copy. Please copy manually.", 3000);
      };

      // Use modern Clipboard API with fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(textToCopy).then(
          showSuccessNotification,
          (err) => {
            console.warn('Async clipboard copy failed, trying fallback:', err);
            if (this._copyFallback(textBox)) {
              showSuccessNotification();
            } else {
              showFailureNotification(new Error('Fallback execCommand failed.'));
            }
          }
        );
      } else {
        // Fallback for older browsers
        console.warn('navigator.clipboard not supported, using fallback.');
        if (this._copyFallback(textBox)) {
          showSuccessNotification();
        } else {
          showFailureNotification(new Error('Fallback execCommand failed.'));
        }
      }
    });
  }

  // Helper function for execCommand copy fallback
  _copyFallback(elementToCopyFrom) {
    if (!elementToCopyFrom) return false;
    try {
        const range = document.createRange();
        range.selectNodeContents(elementToCopyFrom);
        const selection = window.getSelection();
        if (!selection) throw new Error("window.getSelection() failed");
        selection.removeAllRanges();
        selection.addRange(range);
        const successful = document.execCommand('copy');
        selection.removeAllRanges(); // Deselect text
        return successful;
    } catch (fallbackErr) {
        console.error('Copy fallback error:', fallbackErr);
        return false;
    }
  }


  async _prepareShareModal() {
    const textBox = this.dynamicElements.shareTextBox;
    if (!textBox || !this.gameCore.puzzle) return;

    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${month}-${day}-${year}`;

    const gridSize = this.gameCore.gridSize;
    const swapCount = this.gameCore.puzzle.swapCount;
    const isHard = this.gameCore.isHardMode;
    const isUnlimited = this.gameCore.isUnlimitedMode; // State at time of completion/share

    let modeText = '';
    if (!isUnlimited) {
        if (isHard) modeText = ` HARD MODE`;
    } else {
        modeText = ` (Unlimited)`; // Indicate unlimited explicitly
    }

    const shareText = isUnlimited
      ? `Squdle ${gridSize}x${gridSize}: Solved in Unlimited Mode! (${formattedDate})\n\nPlay it at: https://www.squdle.com/?ref=share`
      : `Squdle ${gridSize}x${gridSize}${modeText}: Solved in ${swapCount} swaps! (${formattedDate})\n\nPlay it at: https://www.squdle.com/?ref=share`;

    textBox.textContent = shareText;

    const rankInfoEl = this.dynamicElements.shareRankInfo;
    if (rankInfoEl) {
      if (isUnlimited) {
          rankInfoEl.style.display = 'none'; // Hide rank if unlimited
      } else {
          // Get leaderboard and find rank
          try {
            const scores = await this.storageManager.getLeaderboard(gridSize);
            // Find index matching swaps AND hard mode status
            const userRankIndex = scores.findIndex(score => {
                const s = score.score !== undefined ? score.score : score.swaps;
                const h = score.isHardMode !== undefined ? score.isHardMode : score.hardMode;
                // Use loose equality for 'h' in case backend returns 1/0 and frontend uses true/false
                return s === swapCount && !!h === !!isHard;
            });
            const userRank = userRankIndex + 1; // Rank is 1-based

            if (userRank > 0) {
              rankInfoEl.textContent = `Your rank today (#${userRank} of ${scores.length})`;
              rankInfoEl.style.display = 'block';
            } else {
              rankInfoEl.style.display = 'none'; // Hide if not found (shouldn't happen if score was added)
            }
          } catch (e) {
             console.error("Error fetching rank for share modal:", e);
             rankInfoEl.style.display = 'none';
          }
      }
    }

    // Reset notification state
    const notification = this.dynamicElements.shareCopyNotification;
    if (notification) {
      notification.style.visibility = 'hidden';
      notification.style.opacity = '0';
    }
  }

  // --- Reset Confirmation Logic ---
  showResetConfirmation(onConfirmCallback) {
    const modalId = 'resetConfirm';
    this._hideCustomConfirm(modalId); // Ensure previous is removed

    const confirmModal = this._createModalBase(
      modalId,
      'Reset Puzzle?',
      'fa-exclamation-triangle',
      '#f39c12' // Orange warning color
    );
    this.modals[modalId] = confirmModal; // Store reference

    const modalContent = confirmModal.querySelector('.modal-content');

    const messageElement = document.createElement('p');
    messageElement.textContent = `Are you sure you want to reset the puzzle? Your current progress will be lost.`;
    messageElement.style.marginBottom = '20px';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 15px; justify-content: center;';

    const confirmButton = document.createElement('button');
    confirmButton.textContent = 'Yes, Reset';
    confirmButton.style.backgroundColor = '#e74c3c'; // Red for reset action
    confirmButton.onclick = () => {
      this.hideModal(modalId);
      if (onConfirmCallback) onConfirmCallback();
    };

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.onclick = () => this.hideModal(modalId);

    buttonContainer.appendChild(confirmButton);
    buttonContainer.appendChild(cancelButton);

    modalContent.appendChild(messageElement);
    modalContent.appendChild(buttonContainer);

    document.body.appendChild(confirmModal);
    this.showModal(modalId);

    setTimeout(() => cancelButton.focus(), 100); // Focus cancel by default
  }


  // --- Swap Limit Reached Modal (Standard Mode - Should be unreachable if Hard Mode is off) ---
  // This modal might become obsolete if Hard Mode is the only way to hit a limit.
  // Keeping it for now in case logic changes.
  showSwapLimitModal(maxSwaps, onContinueCallback, onResetCallback) {
    const modalId = 'limitReached';
    this._hideCustomConfirm(modalId); // Ensure previous is removed

    const limitModal = this._createModalBase(
      modalId,
      'Swap Limit Reached!',
      'fa-exclamation-circle',
      '#e74c3c' // Red error color
    );
    this.modals[modalId] = limitModal; // Store reference

    const modalContent = limitModal.querySelector('.modal-content');

    const messageElement = document.createElement('p');
    messageElement.textContent = `You've used all ${maxSwaps} swaps without solving the puzzle.`;
    messageElement.style.marginBottom = '10px';

    const infoElement = document.createElement('p');
    infoElement.innerHTML = `You can <strong>continue playing with unlimited swaps</strong> (leaderboard disabled).`;
    infoElement.style.cssText = 'margin-bottom: 20px; font-size: 0.9rem; color: #ccc;';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 15px; justify-content: center; flex-wrap: wrap;';

    const continueButton = document.createElement('button');
    continueButton.textContent = 'Continue Playing';
    continueButton.style.backgroundColor = '#3498db'; // Blue for continue action
    continueButton.onclick = () => {
      this.hideModal(modalId);
      if (onContinueCallback) onContinueCallback();
    };

    const resetButton = document.createElement('button');
    resetButton.textContent = 'Reset Puzzle';
    resetButton.style.backgroundColor = '#2ecc71'; // Green for reset action
    resetButton.onclick = () => {
      this.hideModal(modalId);
      if (onResetCallback) onResetCallback();
    };

    const closeButton = document.createElement('button');
    closeButton.textContent = 'Close';
    closeButton.onclick = () => this.hideModal(modalId);

    buttonContainer.appendChild(continueButton);
    buttonContainer.appendChild(resetButton);
    buttonContainer.appendChild(closeButton);

    modalContent.appendChild(messageElement);
    modalContent.appendChild(infoElement);
    modalContent.appendChild(buttonContainer);

    document.body.appendChild(limitModal);
    this.showModal(modalId);

    setTimeout(() => continueButton.focus(), 100); // Focus continue by default
  }

  // --- Hard Mode Failure Modal ---
  showHardModeFailModal(maxSwaps, onTryAgainCallback, onSwitchToRegularCallback) {
    const modalId = 'hardModeFail';
    this._hideCustomConfirm(modalId); // Ensure previous is removed

    const failModal = this._createModalBase(
      modalId,
      'Hard Mode Failed!',
      'fa-times-circle', // Different icon for failure
      '#c0392b' // Darker red
    );
    this.modals[modalId] = failModal; // Store reference

    const modalContent = failModal.querySelector('.modal-content');

    const messageElement = document.createElement('p');
    messageElement.textContent = `You've used all ${maxSwaps} swaps without solving the puzzle in Hard Mode.`;
    messageElement.style.marginBottom = '20px';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 15px; justify-content: center; flex-wrap: wrap;';

    const tryAgainButton = document.createElement('button');
    tryAgainButton.id = 'try-again-button'; // ID for potential focus targeting
    tryAgainButton.textContent = 'Try Again (Reset)';
    tryAgainButton.style.backgroundColor = '#e74c3c'; // Red for reset
    tryAgainButton.onclick = () => {
      this.hideModal(modalId);
      if (onTryAgainCallback) onTryAgainCallback();
    };

    const switchToRegularButton = document.createElement('button');
    switchToRegularButton.textContent = 'Switch to Regular';
    switchToRegularButton.style.backgroundColor = '#3498db'; // Blue for switch
    switchToRegularButton.onclick = () => {
      this.hideModal(modalId);
      if (onSwitchToRegularCallback) onSwitchToRegularCallback();
    };

    const closeButton = document.createElement('button');
    closeButton.textContent = 'Close';
    closeButton.onclick = () => this.hideModal(modalId);

    buttonContainer.appendChild(tryAgainButton);
    buttonContainer.appendChild(switchToRegularButton);
    buttonContainer.appendChild(closeButton);

    modalContent.appendChild(messageElement);
    modalContent.appendChild(buttonContainer);

    document.body.appendChild(failModal);
    this.showModal(modalId);

    setTimeout(() => tryAgainButton.focus(), 100); // Focus "Try Again" by default
  }

  // --- Helper for Custom Confirm Dialogs ---
  showCustomConfirm(title, message, onConfirm, onCancel, confirmText = 'Yes', cancelText = 'Cancel', danger = false) {
    const modalId = danger ? 'customConfirmDanger' : 'customConfirmInfo';
    this._hideCustomConfirm(modalId); // Ensure previous is removed

    const confirmModal = this._createModalBase(
      modalId,
      title,
      danger ? 'fa-exclamation-triangle' : 'fa-info-circle', // Icon based on danger
      danger ? '#e74c3c' : '#3498db' // Color based on danger
    );
    this.modals[modalId] = confirmModal; // Store reference

    const modalContent = confirmModal.querySelector('.modal-content');

    const messageElement = document.createElement('p');
    messageElement.textContent = message;
    messageElement.style.marginBottom = '20px';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 15px; justify-content: center;';

    const confirmButton = document.createElement('button');
    confirmButton.id = 'custom-confirm-yes'; // ID for focus targeting
    confirmButton.textContent = confirmText;
    if (danger) confirmButton.style.backgroundColor = '#e74c3c'; // Red if danger
    confirmButton.onclick = () => {
      this.hideModal(modalId);
      if (onConfirm) onConfirm();
    };

    const cancelButton = document.createElement('button');
    cancelButton.id = 'custom-confirm-cancel'; // ID for focus targeting
    cancelButton.textContent = cancelText;
    cancelButton.onclick = () => {
      this.hideModal(modalId);
      if (onCancel) onCancel();
    };

    buttonContainer.appendChild(confirmButton);
    buttonContainer.appendChild(cancelButton);

    modalContent.appendChild(messageElement);
    modalContent.appendChild(buttonContainer);

    document.body.appendChild(confirmModal);
    this.showModal(modalId);

    // Focus confirm if danger, otherwise focus cancel
    setTimeout(() => (danger ? confirmButton : cancelButton).focus(), 100);
  }

  // Private method to hide/remove custom confirms safely
  _hideCustomConfirm(modalId) {
    const modal = this.modals[modalId];
    if (modal) {
      this.hideModal(modalId); // Use standard hide logic which includes cleanup
    }
  }

  // Private method to create modal base structure (DRY)
  _createModalBase(id, title, iconClass = 'fa-info-circle', iconColor = '#3498db') {
    const modal = document.createElement('div');
    modal.id = `${id}`; // Use the modalId directly as the element ID
    modal.className = 'modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', `${id}-title`);
    modal.style.zIndex = '150'; // Ensure dynamic modals are on top

    const modalContent = document.createElement('div');
    modalContent.className = 'modal-content';
    modalContent.setAttribute('tabindex', '-1'); // Make content focusable

    // Add icon if specified
    if (iconClass) {
      const iconElement = document.createElement('i');
      iconElement.className = `fas ${iconClass}`;
      // Use CSS for styling if possible, fallback to inline styles
      Object.assign(iconElement.style, {
        fontSize: '2.5rem', color: iconColor, marginBottom: '15px', display: 'block'
      });
      modalContent.appendChild(iconElement);
    }

    // Add title
    const titleElement = document.createElement('h2');
    titleElement.id = `${id}-title`;
    titleElement.textContent = title;
    titleElement.style.marginBottom = '15px';
    modalContent.appendChild(titleElement);

    modal.appendChild(modalContent);

    // Add backdrop click listener to close
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.hideModal(id); // Use the passed ID for hiding
      }
    });

    return modal;
  }

} // End of ModalManager class
// Removed window export
